import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const BASE = `${API_BASE_URL}/api/ai-paper`;

const headers = (user) => ({
  'x-user-role': user?.role || '',
  'x-user-name': encodeURIComponent(user?.name || ''),
  'x-user-id': String(user?._id || user?.id || '')
});

// Upload with metadata (category/subject/class) – accepts PDF or image
export const uploadFile = (user, file, meta = {}, onProgress) => {
  const fd = new FormData();
  Object.entries(meta).forEach(([k, v]) => v && fd.append(k, v));
  fd.append('file', file); // file last so multer sees the text fields first
  return axios.post(`${BASE}/upload-source`, fd, {
    headers: headers(user),
    timeout: 0,
    onUploadProgress: (e) => onProgress && onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
  }).then((r) => r.data);
};

export const analyzeStyle = (user, id, force = false) =>
  axios.post(`${BASE}/analyze-style/${id}`, { force }, { headers: headers(user), timeout: 180000 }).then((r) => r.data);

export const logDownload = (user, id, kind) =>
  axios.post(`${BASE}/papers/${id}/log-download`, { kind }, { headers: headers(user) }).then((r) => r.data).catch(() => {});

export const getMyStats = (user) => axios.get(`${BASE}/my-stats`, { headers: headers(user) }).then((r) => r.data);
export const listFiles = (user, params = {}) => axios.get(`${BASE}/files`, { headers: headers(user), params }).then((r) => r.data);
export const updateFile = (user, id, patch) => axios.patch(`${BASE}/files/${id}`, patch, { headers: headers(user) }).then((r) => r.data);

// Authenticated download of an original upload (headers can't be sent via a plain <a href>)
export const downloadFile = async (user, id, name) => {
  const res = await axios.get(`${BASE}/files/${id}/download`, { headers: headers(user), responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const adminOverview = (user) => axios.get(`${BASE}/admin/overview`, { headers: headers(user) }).then((r) => r.data);
export const adminTeacher = (user, key) => axios.get(`${BASE}/admin/teacher/${encodeURIComponent(key)}`, { headers: headers(user) }).then((r) => r.data);

export const errMsg = (e) => e?.response?.data?.error || e?.message || 'Something went wrong';

export const getStatus = (user) => axios.get(`${BASE}/status`, { headers: headers(user) }).then((r) => r.data);

export const uploadSource = (user, file, onProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  return axios.post(`${BASE}/upload-source`, fd, {
    headers: headers(user),
    timeout: 0,
    onUploadProgress: (e) => onProgress && onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
  }).then((r) => r.data);
};

export const listSources = (user) => axios.get(`${BASE}/sources`, { headers: headers(user) }).then((r) => r.data);
export const previewSource = (user, id) => axios.get(`${BASE}/source-preview/${id}`, { headers: headers(user) }).then((r) => r.data);
export const deleteSource = (user, id) => axios.delete(`${BASE}/source/${id}`, { headers: headers(user) }).then((r) => r.data);

// Generation can take a while (several AI calls)
export const generatePaper = (user, payload) =>
  axios.post(`${BASE}/generate`, payload, { headers: headers(user), timeout: 300000 }).then((r) => r.data);

export const regenerateQuestion = (user, payload) =>
  axios.post(`${BASE}/regenerate-question`, payload, { headers: headers(user), timeout: 120000 }).then((r) => r.data);

export const savePaper = (user, paper) =>
  (paper._id
    ? axios.put(`${BASE}/papers/${paper._id}`, paper, { headers: headers(user) })
    : axios.post(`${BASE}/papers`, paper, { headers: headers(user) })).then((r) => r.data);

export const listPapers = (user) => axios.get(`${BASE}/papers`, { headers: headers(user) }).then((r) => r.data);
export const getPaper = (user, id) => axios.get(`${BASE}/papers/${id}`, { headers: headers(user) }).then((r) => r.data);
